import * as React from 'react';
import { AuthState } from '../auth/auth.types';
import { Tabs, Tab } from 'material-ui/Tabs';
import Checkbox from 'material-ui/Checkbox';
import TextField from 'material-ui/TextField';
import RaisedButton from 'material-ui/RaisedButton';
import ImageUploadComponent from '../util/ImageUploadComponent';
import SectionPanel from '../util/SectionPanelComponent';
import AddressComponent from '../geo/address.component';
import { AddressInfo, AddressInfoError } from '../geo/address.types';
import SelectField from 'material-ui/SelectField';
import MenuItem from 'material-ui/MenuItem';
import {
  servicePackages,
  M3,
  LOGO_HERE,
  DIRECT_DEPOSIT_UPDATE,
  DIRECT_DEPOSIT_CREATED,
  DIRECT_DEPOSIT_REMAINED,
  generateCreditCardYears,
  CREDIT_CARD_MONTHS,
  CREDIT_CARD_UPDATE,
  CREDIT_CARD_CREATED,
  CREDIT_CARD_REMAINED
} from '../business/business.constants';
import { BillingDetailsMode } from '../billing/billing.types';
import {
  ServicePackage,
  ServicePackageConstant, 
  CreateUpdateRequestParameters,
  DirectDepositStatus,
  CreditCardStatus,
  BusinessState,
  Statement
} from '../business/business.types';
import { countries } from '../geo/geo.const';
import { CountryInfo, CountryCode } from '../geo/geo.types';
import {
  isValidBusinessName,
  isValidName,
  validateCreditCardNumber,
  isValidPhone
} from '../util/Validation';
import RefreshIndicator from 'material-ui/RefreshIndicator';
import * as moment_timezone from 'moment-timezone';
import { isAdminAccount, isImpersonating } from '../admin/Util';
import  { AdminState } from '../admin/admin.types';
import { CreditCardType } from '../payments/Types';
import { 
  NONE, 
  CREDIT_CARD_LENGTH, 
  AMEX_LENGTH,
  MAX_BANK_ACCOUNT_LENGTH,
  FINANCIAL_INSTITUTION_LENGTH,
  TRANSIT_NUMBER_LENGTH
} from '../payments/Constants';
import { isAuthTokenExpired } from '../commons/AuthTokenValidator';
import { renderCreditCardIcon, parseCreditCardType } from '../creditcard/CreditCardIconHelper';
import DataTables from 'material-ui-datatables';
import { getPaginatedList } from '../util/pagination';
import * as html2canvas from 'html2canvas';
import * as jspdf from 'jspdf';
import Phone from 'react-phone-number-input';
import { FormattedNumber } from 'react-intl';
import CircularProgress from 'material-ui/CircularProgress';
import Chip from 'material-ui/Chip';
import CommonFeatures from '../commons/CommonFeatures';
import { Link } from 'react-router-dom';
import * as validator from 'validator';
import Dialog from 'material-ui/Dialog';
import { FlatButton } from 'material-ui';
import * as creditcardutils from 'creditcardutils';
import ReactTable from 'react-table';
import { cannotWrite, cannotUpdate } from '../util/permission.util';
import { BUSINESS } from '../account/Constants';

const NAME_ERROR_MESSAGE =
  'Only alphabet, dash, and/or space are allowed in name. And name has to be shorter than 30 chars long.';
const DEFAULT_ROW_SIZE = 5;

type Props = {
  readonly authState: AuthState
  readonly adminState: AdminState
  readonly businessState: BusinessState
  readonly updateBusiness: (requestParam: CreateUpdateRequestParameters) => Promise<Response>
  readonly validateBusinessPhoneNumber: (phone: string) => Promise<Response>
  readonly toAdminDashboard: () => void
  readonly getStatements: () => Promise<Response>
};

type States = {
  readonly addressInfo: AddressInfo
  readonly editedAddressInfo: AddressInfo
  readonly addressInfoError: AddressInfoError
  readonly servicePackage: ServicePackage
  readonly editedServicePackage: ServicePackage
  readonly accountHolderName: string | undefined
  readonly editedAccountHolderName: string | undefined
  readonly accountHolderNameErrorMessage: string
  readonly financialInstitutionName: string | undefined
  readonly editedFinancialInstitutionName: string | undefined
  readonly financialInstitutionNameErrorMessage: string
  readonly transitNumber: string | undefined
  readonly editedTransitNumber: string | undefined
  readonly transitNumberErrorMessage: string
  readonly financialInstitutionNumber: string | undefined
  readonly editedFinancialInstitutionNumber: string | undefined
  readonly financialInstitutionNumberErrorMessage: string
  readonly bankAccountNumber: string | undefined
  readonly editedBankAccountNumber: string | undefined
  readonly bankAccountNumberErrorMessage: string
  readonly payMethodsDetailsMode: BillingDetailsMode
  readonly businessCode: string
  readonly name: string
  readonly editedName: string
  readonly nameErrorMessage: string
  readonly updated: boolean
  readonly loading: boolean
  readonly businessLogoUrl: string
  readonly businessLogoFile: File | undefined
  readonly fileSizeError: string
  readonly pointPerAttendance: number
  readonly editedPointPerAttendance: number
  readonly smsDelay: number
  readonly editedSmsDelay: number
  readonly pointPerAttendanceErrorMessage: string
  readonly cardNumber: string | undefined
  readonly editedCardNumber: string | undefined
  readonly cardNumberErrorMessage: string
  readonly cardHolderName: string | undefined
  readonly editedCardHolderName: string | undefined
  readonly cvc: string | undefined
  readonly editedCvc: string | undefined
  readonly expiryMonth: string | undefined
  readonly editedExpiryMonth: string | undefined
  readonly expiryYear: string | undefined
  readonly editedExpiryYear: string | undefined
  readonly invalidCreditCardErrorMessage: string,
  readonly creditcardType: CreditCardType
  readonly loadedStatements: boolean
  readonly currentPage: number
  readonly rowSize: number
  readonly downloadPdf: boolean
  readonly selectedRow: number
  readonly newTag: string
  readonly tags: string[]
  readonly editedTags: string[]
  readonly newEmail: string
  readonly newEmailErrorMessage: string
  readonly failedCheckinNotificationCcEmails: string[]
  readonly editedFailedCheckinNotificationCcEmails: string[]
  readonly newPhoneNumber: string
  readonly newPhoneNumberErrorMessage: string
  readonly failedCheckinNotificationCcPhoneNumbers: string[]
  readonly editedFailedCheckinNotificationCcPhoneNumbers: string[]
  readonly fileFormatError: string
  readonly failedCheckinEmail: boolean
  readonly editedFailedCheckinEmail: boolean
  readonly failedCheckinSms: boolean
  readonly editedFailedCheckinSms: boolean
  readonly openPdfPreviewModal: boolean
  readonly downloading: boolean
  readonly smsDelaysErrorMessage: string
  readonly timeBasedMultipleCheckin: boolean
  readonly editedTimeBasedMultipleCheckin: boolean
  readonly sendCheckinNotificationToMobile2: boolean
  readonly editedSendCheckinNotificationToMobile2: boolean
};

class BusinessInfoComponent extends React.Component<Props, States> {

  constructor(props: Props) {
    super(props);

    this.state = {
      addressInfo: this.getAddressInfo(props.businessState),
      editedAddressInfo: this.getAddressInfo(props.businessState),
      addressInfoError: {
        addressError: '',
        cityError: '',
        countryError: '',
        provinceError: '',
        postalCodeError: '',
        phoneError: '',
        mobileError: '',
      },
      servicePackage: props.businessState.business ? 
        props.businessState.business.servicePackage : 
        M3,
      editedServicePackage: props.businessState.business ? 
        props.businessState.business.servicePackage : 
        M3,
      accountHolderName: this.isThereDirectDeposit(props) ?
        props.businessState.business!.directDeposit!.accountHolderName :
        undefined,
      editedAccountHolderName: this.isThereDirectDeposit(props) ?
        props.businessState.business!.directDeposit!.accountHolderName :
        undefined,
      accountHolderNameErrorMessage: '',
      financialInstitutionName: this.isThereDirectDeposit(props) ?
        props.businessState.business!.directDeposit!.financialInstitutionName :
        undefined,
      editedFinancialInstitutionName: this.isThereDirectDeposit(props) ?
        props.businessState.business!.directDeposit!.financialInstitutionName :
        undefined,
      financialInstitutionNameErrorMessage: '',
      transitNumber: this.isThereDirectDeposit(props) ?
        props.businessState.business!.directDeposit!.transitNumber :
        undefined,
      editedTransitNumber: this.isThereDirectDeposit(props) ?
        props.businessState.business!.directDeposit!.transitNumber :
        undefined,
      financialInstitutionNumber: this.isThereDirectDeposit(props) ?
        props.businessState.business!.directDeposit!.financialInstitutionNumber :
        undefined,
      editedFinancialInstitutionNumber: this.isThereDirectDeposit(props) ?
        props.businessState.business!.directDeposit!.financialInstitutionNumber :
        undefined,
      bankAccountNumber: this.isThereDirectDeposit(props) ?
        props.businessState.business!.directDeposit!.bankAccountNumber :
        undefined,
      editedBankAccountNumber: this.isThereDirectDeposit(props) ?
        props.businessState.business!.directDeposit!.bankAccountNumber :
        undefined,
      bankAccountNumberErrorMessage: '',
      financialInstitutionNumberErrorMessage: '',
      transitNumberErrorMessage: '',
      payMethodsDetailsMode: 'CREATE',
      name: props.businessState.business ? props.businessState.business.name : '',
      editedName: props.businessState.business ? props.businessState.business.name : '',
      nameErrorMessage: '',
      updated: false,
      loading: false,
      businessLogoUrl: props.businessState.business && props.businessState.business.picUrl ?
        props.businessState.business.picUrl :
        '',
      businessLogoFile: undefined,
      fileSizeError: '',
      businessCode: props.businessState.business ? 
        props.businessState.business.businessCode :
        '',
      pointPerAttendance: props.businessState.business ? props.businessState.business.pointPerAttendance : 0,
      editedPointPerAttendance: props.businessState.business ? props.businessState.business.pointPerAttendance : 0,
      smsDelay: props.businessState.business ? props.businessState.business.smsDelay : 0,
      editedSmsDelay: props.businessState.business ? props.businessState.business.smsDelay : 0,
      pointPerAttendanceErrorMessage: '',
      smsDelaysErrorMessage: '',
      cardNumber: this.isThereCreditCard(props) ?
        props.businessState.business!.creditCard!.cardNumber :
        undefined,
      editedCardNumber: this.isThereCreditCard(props) ?
        props.businessState.business!.creditCard!.cardNumber :
        undefined,
      cardNumberErrorMessage: '',
      cardHolderName: this.isThereCreditCard(props) ?
        props.businessState.business!.creditCard!.cardHolderName :
        undefined,
      editedCardHolderName: this.isThereCreditCard(props) ?
        props.businessState.business!.creditCard!.cardHolderName :
        undefined,
      cvc: this.isThereCreditCard(props) ?
        props.businessState.business!.creditCard!.cvc :
        undefined,
      editedCvc: this.isThereCreditCard(props) ?
        props.businessState.business!.creditCard!.cvc :
        undefined,
      expiryMonth: this.isThereCreditCard(props) ?
        props.businessState.business!.creditCard!.expiryMonth :
        undefined,
      editedExpiryMonth: this.isThereCreditCard(props) ?
        props.businessState.business!.creditCard!.expiryMonth :
        undefined,
      expiryYear: this.isThereCreditCard(props) ?
        props.businessState.business!.creditCard!.expiryYear :
        undefined,
      editedExpiryYear: this.isThereCreditCard(props) ?
        props.businessState.business!.creditCard!.expiryYear :
        undefined,
      invalidCreditCardErrorMessage: '',
      creditcardType: NONE,
      loadedStatements: false,
      currentPage: 1,
      rowSize: 5,
      downloadPdf: false,
      selectedRow: 0,
      newTag: '',
      tags: this.props.businessState.business ? this.props.businessState.business.tags : [],
      editedTags: this.props.businessState.business ? this.props.businessState.business.tags : [],
      newEmail: '',
      newEmailErrorMessage: '',
      failedCheckinNotificationCcEmails: this.props.businessState.business ? 
      this.props.businessState.business.failedCheckinNotificationCcEmails : [],
      editedFailedCheckinNotificationCcEmails: this.props.businessState.business ? 
      this.props.businessState.business.failedCheckinNotificationCcEmails : [],
      newPhoneNumber: '',
      newPhoneNumberErrorMessage: '',
      failedCheckinNotificationCcPhoneNumbers: this.props.businessState.business ? 
      this.props.businessState.business.failedCheckinNotificationCcPhoneNumbers : [],
      editedFailedCheckinNotificationCcPhoneNumbers: this.props.businessState.business ? 
      this.props.businessState.business.failedCheckinNotificationCcPhoneNumbers : [],
      fileFormatError: '',
      failedCheckinEmail: props.businessState.business ? 
        props.businessState.business.failedCheckinEmail : 
        false,
      editedFailedCheckinEmail: props.businessState.business ? 
        props.businessState.business.failedCheckinEmail : 
        false,
      failedCheckinSms: props.businessState.business ? 
        props.businessState.business.failedCheckinSms : 
        false,
      editedFailedCheckinSms: props.businessState.business ? 
        props.businessState.business.failedCheckinSms : 
        false,
      openPdfPreviewModal: false,
      downloading: false,
      timeBasedMultipleCheckin: props.businessState.business ?
        props.businessState.business.timeBasedMultipleCheckin :
        false,
      editedTimeBasedMultipleCheckin: props.businessState.business ?
        props.businessState.business.timeBasedMultipleCheckin :
        false,
      sendCheckinNotificationToMobile2 : props.businessState.business ?
        props.businessState.business.sendCheckinNotificationToMobile2 :
        false,
      editedSendCheckinNotificationToMobile2 : props.businessState.business ?
        props.businessState.business.sendCheckinNotificationToMobile2 :
        false
    };

    this.isFieldEdited = this.isFieldEdited.bind(this);
    this.getAddressInfo = this.getAddressInfo.bind(this);
    this.onServicePackageChange = this.onServicePackageChange.bind(this);
    this.filterAndGetCountryCode  = this.filterAndGetCountryCode.bind(this);
    this.handleBusinessNameChange = this.handleBusinessNameChange.bind(this);
    this.addressInfoChangeHandler = this.addressInfoChangeHandler.bind(this);
    this.isAddressInfoChanged = this.isAddressInfoChanged.bind(this);
    this.handleBusinessLogoFileChange = this.handleBusinessLogoFileChange.bind(this);
    this.handleUnselectImage = this.handleUnselectImage.bind(this);
    this.handleAccountHolderNameChange = this.handleAccountHolderNameChange.bind(this);
    this.isThereCreditCard = this.isThereCreditCard.bind(this);
    this.isThereDirectDeposit = this.isThereDirectDeposit.bind(this);
    this.handleFinancialInstitutionNameChange = this.handleFinancialInstitutionNameChange.bind(this);
    this.handleTransitNumberChange = this.handleTransitNumberChange.bind(this);
    this.handleFinancialInstitutionNumberChange = this.handleFinancialInstitutionNumberChange.bind(this);
    this.handleBankAccountNumberChange = this.handleBankAccountNumberChange.bind(this);
    this.setPhoneErrorMessage = this.setPhoneErrorMessage.bind(this);
    this.isPhoneNumberEdited = this.isPhoneNumberEdited.bind(this);
    this.isMobileNumberEdited = this.isMobileNumberEdited.bind(this);
    this.validateTransitNumber = this.validateTransitNumber.bind(this);
    this.validateFinancialInstitutionNumber = this.validateFinancialInstitutionNumber.bind(this);
    this.validateBankAccountNumber = this.validateBankAccountNumber.bind(this);
    this.handlePointPerAttendance = this.handlePointPerAttendance.bind(this);
    this.handleCardNumberChange = this.handleCardNumberChange.bind(this);
    this.handlecardHolderNameChange = this.handlecardHolderNameChange.bind(this);
    this.handleCvcChange = this.handleCvcChange.bind(this);
    this.onExpiryMonthChange = this.onExpiryMonthChange.bind(this);
    this.onExpiryYearChange = this.onExpiryYearChange.bind(this);
    this.isCreditCardInfoFilled = this.isCreditCardInfoFilled.bind(this);
    this.selectCreditCardStatus = this.selectCreditCardStatus.bind(this);
    this.selectDirectDepositStatus = this.selectDirectDepositStatus.bind(this);
    this.getDirectDepositIdForUpdating = this.getDirectDepositIdForUpdating.bind(this);
    this.getCreditCardIdForUpdating = this.getCreditCardIdForUpdating.bind(this);
  }

  componentDidMount() {
    const { authState, toAdminDashboard, getStatements, adminState } = this.props;
    
    if (isAdminAccount(authState) && !isImpersonating(adminState)) {
      return toAdminDashboard();
    }

    getStatements();
  }

  componentWillReceiveProps(nextProps: Props) {
    if (this.props !== nextProps && !isAuthTokenExpired() && nextProps.authState.statusCode === 200) {
      const { businessState } = nextProps;

      this.setPhoneErrorMessage(businessState);

      if (!this.state.loadedStatements) {
        this.setState(
          {loadedStatements: true},
          nextProps.getStatements
        );
      }

      if (nextProps.businessState.statusCode === 417) {
        this.setState({
          ...this.state,
          invalidCreditCardErrorMessage: 'Something went wrong. Please check Credit Card inputs again.'
        });
      } else if (nextProps.businessState.statusCode === 406) {
        this.setState({
          ...this.state,
          invalidCreditCardErrorMessage: 'Failed to register payment method because the received card was invalid.'
        });
      } else {
        this.setState({
          addressInfo: this.getAddressInfo(nextProps.businessState),
          servicePackage: nextProps.businessState.business ? 
            nextProps.businessState.business.servicePackage : 
            M3,
          editedServicePackage: nextProps.businessState.business ? 
            nextProps.businessState.business.servicePackage : 
            M3,
          accountHolderName: this.isThereDirectDeposit(nextProps) ?
            nextProps.businessState.business!.directDeposit!.accountHolderName :
            '',
          editedAccountHolderName: this.isThereDirectDeposit(nextProps) ?
            nextProps.businessState.business!.directDeposit!.accountHolderName :
            '',
          financialInstitutionName: this.isThereDirectDeposit(nextProps) ?
            nextProps.businessState.business!.directDeposit!.financialInstitutionName :
            '',
          editedFinancialInstitutionName: this.isThereDirectDeposit(nextProps) ?
            nextProps.businessState.business!.directDeposit!.financialInstitutionName :
            '',
          transitNumber: this.isThereDirectDeposit(nextProps) ?
            nextProps.businessState.business!.directDeposit!.maskedTransitNumber :
            '',
          editedTransitNumber: this.isThereDirectDeposit(nextProps) ?
            nextProps.businessState.business!.directDeposit!.maskedTransitNumber :
            '',
          financialInstitutionNumber: this.isThereDirectDeposit(nextProps) ?
            nextProps.businessState.business!.directDeposit!.financialInstitutionNumber :
            '',
          editedFinancialInstitutionNumber: this.isThereDirectDeposit(nextProps) ?
            nextProps.businessState.business!.directDeposit!.financialInstitutionNumber :
            '',
          bankAccountNumber: this.isThereDirectDeposit(nextProps) ?
            nextProps.businessState.business!.directDeposit!.maskedBankAccountNumber :
            '',
          editedBankAccountNumber: this.isThereDirectDeposit(nextProps) ?
            nextProps.businessState.business!.directDeposit!.maskedBankAccountNumber :
            '',
          name: nextProps.businessState.business ? nextProps.businessState.business.name : '',
          editedName: nextProps.businessState.business ? nextProps.businessState.business.name : '',
          businessLogoUrl: nextProps.businessState.business && nextProps.businessState.business.picUrl ?
            nextProps.businessState.business.picUrl :
            '',
          businessCode: nextProps.businessState.business ? 
            nextProps.businessState.business.businessCode :
            '',
          pointPerAttendance: nextProps.businessState.business ?
            nextProps.businessState.business.pointPerAttendance :
            0,
          editedPointPerAttendance: nextProps.businessState.business ?
            nextProps.businessState.business.pointPerAttendance :
            0,
          cardNumber: this.isThereCreditCard(nextProps) ?
            nextProps.businessState.business!.creditCard!.cardNumber :
            '',
          editedCardNumber: this.isThereCreditCard(nextProps) ?
            nextProps.businessState.business!.creditCard!.cardNumber :
            '',
          cardHolderName: this.isThereCreditCard(nextProps) ?
            nextProps.businessState.business!.creditCard!.cardHolderName :
            '',
          editedCardHolderName: this.isThereCreditCard(nextProps) ?
            nextProps.businessState.business!.creditCard!.cardHolderName :
            '',
          cvc: this.isThereCreditCard(nextProps) ?
            nextProps.businessState.business!.creditCard!.cvc :
            '',
          editedCvc: this.isThereCreditCard(nextProps) ?
            nextProps.businessState.business!.creditCard!.cvc :
            '',
          expiryMonth: this.isThereCreditCard(nextProps) ?
            nextProps.businessState.business!.creditCard!.expiryMonth :
            '',
          editedExpiryMonth: this.isThereCreditCard(nextProps) ?
            nextProps.businessState.business!.creditCard!.expiryMonth :
            '',
          expiryYear: this.isThereCreditCard(nextProps) ?
            nextProps.businessState.business!.creditCard!.expiryYear :
            '',
          editedExpiryYear: this.isThereCreditCard(nextProps) ?
            nextProps.businessState.business!.creditCard!.expiryYear :
            '',
        });
      }
    }
  }

  componentWillUnmount() {
    this.setState(
      {
        loadedStatements: false, 
        fileSizeError: '', 
        fileFormatError: '',
        openPdfPreviewModal: false
      }
    );
  }

  render() {
    const { editedName, loading, updated } = this.state;
    const { authState, businessState } = this.props;

    return(
      <CommonFeatures
        authState={this.props.authState}
        businessState={this.props.businessState}
      >
        <div className="page-header-row row mt-2">
          <div className="col-auto">
            <h3>Business Information</h3>
          </div>
        </div>
        {this.renderBusinessLogoPicture()}
        <div className="row ma2">
          <div className="col-auto ml-auto mr-auto">
            <h4 className="float-left"><strong>
              {editedName}
            </strong></h4>
          </div>
        </div>
        <div className="row">
          <div className="col-xl-1 col-lg-hidden" />
          <div className="col-xl-10 col-lg-12">
            {this.renderBusinessInfoTabs()}
          </div>
          <div className="col-xl-1 col-lg-hidden" />
        </div>
        <div className="row pl4 pt4 pb4">
          <div className="fl w-80 text-center">
            {
              (updated && authState.statusCode === 200 &&
              !(businessState.statusCode === 417 || businessState.statusCode === 406)) ?
              <div className="animated fadeIn green">
                Update Successful
              </div> :
              <div className="animated fadeIn red">
                {this.state.invalidCreditCardErrorMessage}
              </div>
            }
          </div>
          <div className="fl w-20">
            {
              loading ?
              <div className="mb-5">
                <RefreshIndicator
                  size={40}
                  left={10}
                  top={0}
                  status="loading"
                  style={{
                    display: 'inline-block',
                    position: 'relative',
                  }}
                />
              </div> :
              <div className="mb-5">
                <RaisedButton
                  label="UPDATE"
                  primary={true} 
                  disabled={this.isThereError(this.state) || !this.isFieldEdited(this.state)}
                  onClick={this.onUpdateClick}
                />
              </div>
            }
          </div>
        </div>
        <div className="row mt-4 mb-4 animated fadeIn justify-content-center">
          <div className="col-12">
            <div className="alert alert-secondary" role="alert">
              By proceeding with payment, you agree to our&nbsp;
              {this.renderServiceAgreementLink('Service Agreement')} and&nbsp;
              {this.renderServiceAgreementLink('Privacy Policy')}
            </div>
          </div>
        </div>
        {this.renderPdfPreviewModal()}
      </CommonFeatures>
    );
  }

  private renderBusinessLogoPicture = (): JSX.Element => {
    const { businessLogoUrl } = this.state;

    return(
      <div className="row ma2">
        <div className="col-12">
          <ImageUploadComponent
            defaultImageUrl={businessLogoUrl ? businessLogoUrl : LOGO_HERE}
            onImageFileChangeHandler={this.handleBusinessLogoFileChange}
            unselectImageHandler={this.handleUnselectImage}
          />
        </div>
      </div>
    );
  }

  private renderBusinessInfoTabs = (): JSX.Element => {

    return (
      <Tabs>
        <Tab label="Business Info">
          {this.renderBusinessInformationTab()}
        </Tab>
        <Tab label="Payment Info">
          {this.renderPaymentInformationTab()}
        </Tab>
        <Tab label="Configurations" >
          {this.renderConfigurationsTab()}
        </Tab>
      </Tabs>
    );
  }

  private renderBusinessInformationTab = (): JSX.Element => {
    const {
      editedServicePackage,
      editedAddressInfo,
      addressInfoError,
      editedName,
      nameErrorMessage,
      businessCode
    } = this.state;

    const {
      validateBusinessPhoneNumber,
      authState,
      adminState
    } = this.props;

    return (
      <div className="row ma2">
        <div className="col-12">
          <SectionPanel
            className="row mt-5 mb-5"
            title="General Information"
            titleAlign="center"
          >
            <div className="col-12 p-5">
              <div className="row pa2">
                <div className="col-12">
                  <TextField
                    hintText="Enter Business Name"
                    floatingLabelText="Business Name"
                    value={editedName}
                    onChange={this.handleBusinessNameChange}
                    disabled={cannotWrite(BUSINESS, authState, adminState) && 
                      cannotUpdate(BUSINESS, authState, adminState)}
                    errorText={nameErrorMessage}
                    fullWidth={true}
                  />
                </div>
              </div>
              <div className="row pa2">
                <div className="col-12">
                  <SelectField
                    value={editedServicePackage}
                    hintText="Select Service Package"
                    floatingLabelText="Service Package"
                    disabled={true}
                    onChange={this.onServicePackageChange}
                    fullWidth={true}
                  >
                    {servicePackages.map((sp: ServicePackageConstant, index: number) => {
                      return <MenuItem key={index} value={sp.value} primaryText={sp.name} />;
                    })}
                  </SelectField>
                </div>
              </div>
              <div className="row pa2">
                <div className="col-12">
                  <TextField
                    disabled={true}
                    floatingLabelText="Business Code (Display Only)"
                    value={businessCode}
                    fullWidth={true}
                  />
                </div>
              </div>
            </div>
          </SectionPanel>
          <SectionPanel
            className="row mt-5 mb-5"
            title="Address Information"
            titleAlign="center"
          >
            <div className="col-12 p-5">
              <AddressComponent
                addressInfo={editedAddressInfo}
                addressInfoError={addressInfoError}
                addressInfoChangeHandler={this.addressInfoChangeHandler}
                validatePhoneNumber={true}
                isPhoneNumberEdited={this.isPhoneNumberEdited()}
                validator={validateBusinessPhoneNumber}
                isBusinessRegistration={true}
                businessState={this.props.businessState}
                authState={this.props.authState}
                adminState={this.props.adminState}
                isBusinessInfo={true}
              />
            </div>
          </SectionPanel>
        </div>
      </div>
    );
  }

  private renderPaymentInformationTab = (): JSX.Element => {
    const {
      businessState
    } = this.props;

    return (
      <div className="row ma2">
        <div className="col-12">
          <SectionPanel
            className="row mt-5 mb-5"
            title="Direct Deposit"
            titleAlign="center"
          >
            <div className="col-12 p-5">
              {this.renderDirectDepositForm()}
            </div>
          </SectionPanel>
          <SectionPanel
            className="row mt-5 mb-5"
            title="Credit Card"
            titleAlign="center"
          >
            <div className="col-12 p-5">
              {this.renderCreditCardForm()}
            </div>
          </SectionPanel>
          <SectionPanel
            className="row mt-5 mb-5"
            title="Statements"
            titleAlign="center"
          >
            <div className="col-12 p-5">
              <ReactTable
                data={businessState.statements
                  .map((statement: Statement) => {
                    return {date: new Date(statement.returnedDate).toLocaleDateString()};
                  })
                }
                columns={[
                  {
                    Header: 'Date',
                    accessor: 'date',
                    style: {textAlign: 'center'}
                  }
                ]}
                defaultPageSize={this.state.rowSize}
                className="-striped -highlight"
                getTdProps={(state, rowInfo, column, instance) => {
                  return {
                    onClick: (e, handleOriginal) => rowInfo ? this.handleCellClick(rowInfo.index) : null
                  };
                }}    
              />
            </div>
          </SectionPanel>
        </div>
      </div>
    );
  }

  private handleCellClick = (index) => {
    const { businessState } = this.props;

    return businessState.statements[index] &&
      this.setState(
        {
          openPdfPreviewModal: true,
          downloadPdf: true,
          selectedRow: index
        }
      );
  }

  private renderPdfPreviewModal = (): JSX.Element => {
    if (this.state.openPdfPreviewModal) {
      return(
        <Dialog
          title="Statement PDF Preview"
          actions={[
            this.renderCloseButton(),
            this.renderDownloadButton()
          ]}
          modal={true}
          open={this.state.openPdfPreviewModal}
          contentStyle={{width: '250mm', maxWidth: 'none'}}
          autoScrollBodyContent={true}
        >
          {this.renderPdfToDownload()}
        </Dialog>
      );
    } else {
      return(null);
    }
  }

  private renderCloseButton = (): JSX.Element => {
    return(
      <FlatButton label="Close" onClick={() => this.setState({openPdfPreviewModal: false})} />
    );
  }

  private renderDownloadButton = (): JSX.Element => {
    if (this.state.downloading) {
      return(<CircularProgress size={40} thickness={3} />);
    } else {
      return(
        <FlatButton 
          label="Download"
          primary={true}
          onClick={() => this.setState({downloading: true}, this.printStatement)}
        />
      );
    }
  }

  private renderConfigurationsTab = (): JSX.Element => {
    const {
      pointPerAttendanceErrorMessage,
      editedPointPerAttendance,
      editedSmsDelay,
      smsDelaysErrorMessage
    } = this.state;

    const { authState, adminState } = this.props;

    return (
      <div className="row ma2">
        <div className="col-12">
          <SectionPanel
            className="row mt-5 mb-5"
            title="Configurations"
            titleAlign="center"
          >
            <div className="col-12 p-5">
              <div className="row pa2">
                <div className="col-6">
                  <TextField
                    hintText="Enter Point Per Attendance"
                    floatingLabelText="Point Per Attendance"
                    value={editedPointPerAttendance}
                    onChange={this.handlePointPerAttendance}
                    errorText={pointPerAttendanceErrorMessage}
                    fullWidth={true}
                    disabled={cannotWrite(BUSINESS, authState, adminState) && 
                      cannotUpdate(BUSINESS, authState, adminState)}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-lg-4 col-md-12 col-sm-12">
                  <TextField
                    fullWidth={true}
                    floatingLabelText="New Tag"
                    value={this.state.newTag}
                    onChange={this.handleNewTagChange}
                    disabled={cannotWrite(BUSINESS, authState, adminState) && 
                      cannotUpdate(BUSINESS, authState, adminState)}
                  />
                  <RaisedButton
                    label="ADD TAG"
                    primary={true} 
                    disabled={this.state.newTag === '' || (cannotWrite(BUSINESS, authState, adminState) && 
                      cannotUpdate(BUSINESS, authState, adminState))}
                    onClick={this.onAddTagClick}
                    className="float-left mt-2"
                  />
                </div>
                <div className="col-lg-8 col-md-12 col-sm-12 pt-4">
                  {this.state.editedTags.map(this.renderAddedTags)}
                </div>
              </div>
              <div className="row">
                <div className="col-lg-4 col-md-12 col-sm-12">
                  <TextField
                    fullWidth={true}
                    floatingLabelText="New Failed Check-in Notification CC Email"
                    value={this.state.newEmail}
                    errorText={this.state.newEmailErrorMessage}
                    onChange={this.handleNewEmailChange}
                    disabled={cannotWrite(BUSINESS, authState, adminState) && 
                      cannotUpdate(BUSINESS, authState, adminState)}
                  />
                  <RaisedButton
                    label="ADD Email"
                    primary={true} 
                    disabled={this.state.newEmailErrorMessage !== '' || 
                      this.state.newEmail === '' || (cannotWrite(BUSINESS, authState, adminState) && 
                      cannotUpdate(BUSINESS, authState, adminState))}
                    onClick={this.onAddEmailClick}
                    className="float-left mt-2"
                  />
                </div>
                <div className="col-lg-8 col-md-12 col-sm-12 pt-4">
                  {this.state.editedFailedCheckinNotificationCcEmails.map(this.renderAddedEmails)}
                </div>
              </div>
              <div className="row pt4">
                <div className="col-md-6 col-sm-12">
                  <Checkbox
                    label="Send email notification for failed check-in"
                    checked={this.state.editedFailedCheckinEmail}
                    onCheck={this.handleFailedCheckinEmailCheckboxChange}
                    disabled={cannotWrite(BUSINESS, authState, adminState) && 
                      cannotUpdate(BUSINESS, authState, adminState)}
                  />
                </div>
              </div>
              <div className="row pt4">
                <div className="col-md-6 col-sm-12">
                  <Checkbox
                    label="Allow a time based member to check in multiple times on the same day"
                    checked={this.state.editedTimeBasedMultipleCheckin}
                    onCheck={this.handleTimeBasedMultipleCheckInCheckboxChange}
                    disabled={cannotWrite(BUSINESS, authState, adminState) && 
                      cannotUpdate(BUSINESS, authState, adminState)}
                  />
                </div>
              </div>
            </div>
          </SectionPanel>
        </div>
      </div>
    );
  }

  private renderServiceAgreementLink = (text: String): JSX.Element => {
    return(
      <Link
        to="/service-agreement"
      >
        {text}
      </Link>
    );
  }

  private onAddTagClick = (): void =>
    this.setState({
      editedTags: [this.state.newTag, ...this.state.editedTags.filter((n: string) => n !== this.state.newTag)],
      newTag: ''
    })

  private handleNewTagChange = (event: React.FormEvent<HTMLInputElement>, newValue: string): void =>
    this.setState({newTag: newValue})

  private onAddEmailClick = (): void =>
    
    this.setState({
      editedFailedCheckinNotificationCcEmails: this.state.newEmailErrorMessage === '' ? 
      [this.state.newEmail, ...this.state.editedFailedCheckinNotificationCcEmails.
        filter((n: string) => n !== this.state.newEmail)] : 
      this.state.editedFailedCheckinNotificationCcEmails,
      newEmail: ''
    })

  private handleNewEmailChange = (event: React.FormEvent<HTMLInputElement>, newValue: string): void =>
    this.setState({
      newEmail: newValue,
      newEmailErrorMessage : validator.isEmail(newValue) ? '' : 'Enter valid email'
    })
  
  private onAddPhoneNumberClick = (): void =>
    this.setState({
      editedFailedCheckinNotificationCcPhoneNumbers: this.state.newPhoneNumberErrorMessage === '' ? 
      [this.state.newPhoneNumber, ...this.state.editedFailedCheckinNotificationCcPhoneNumbers.
        filter((n: string) => n !== this.state.newPhoneNumber)] : 
        this.state.editedFailedCheckinNotificationCcPhoneNumbers,
      newPhoneNumber : ''
    })

  private handleNewPhoneNumberChange = (newValue: string): void =>
    this.setState({
      newPhoneNumber: newValue,
      newPhoneNumberErrorMessage: isValidPhone(newValue, 'Enter valid Phone number.')
    })

  private fillEmptyRows = (statementDates: Array<{date: string}>): Array<{date: string}> => {
    const emptyRows = Array.from({length: DEFAULT_ROW_SIZE - this.props.businessState.statements.length})
      .map((index: number) => {
        return({date: ''});
      });

    return [...statementDates, ...emptyRows];
  }

  private renderPdfToDownload = (): JSX.Element => {
    const { businessState } = this.props;

    if (this.state.downloadPdf) {
      return(
        <div
          id="divToPrint"
          className="bg-white"
          style={{
            width: '210mm',
            height: '297mm',
            marginLeft: 'auto',
            marginRight: 'auto'
          }}
        >
          <div className="row no-gutters pt4 pb2 pl5">
            <div className="col-5 text-center">
              <img src={require('../img/Logo-Fin-large.png')}/>
            </div>
          </div>
          <div className="row no-gutters pl5">
            <div className="col-10">
              30 Wertheim Crt. Unit 12, Richmond Hill, ON, L4B 1B9 <br/>
              (905) 597-5554 <br/>
              (833) 597-5554 Toll Free <br/>
            </div>
          </div>
          <div className="row no-gutters pl5">
            <div className="col-7 pt2 pl0">
              <h3 className="font-weight-bold pl0">M3 ACCOUNT STATEMENT</h3>
            </div>
          </div>
          <div className="row no-gutters pl5">
            <div className="col-6 pl0" style={{color: '#FF0000'}}>
              Statement Date : {
                new Date(this.getSelectedStatement().returnedDate).toLocaleDateString()
              }
            </div>
          </div>
          <div className="row no-gutters pl5">
            <div className="col-4">
              <div className="row no-gutters">
                <div className="col-12" style={{color: '#000000'}}>
                  {businessState.business.name}
                </div>
              </div>
              <div className="row no-gutters">
                <div className="col-12">
                  {businessState.business.address}
                </div>
              </div>
              <div className="row no-gutters">
                <div className="col-12">
                  {businessState.business.city},
                  {businessState.business.province},
                  {businessState.business.country}
                </div>
              </div>
              <div className="row no-gutters">
                <div className="col-12">
                  <Phone
                    countries={[businessState.business.country]}
                    value={businessState.business.phone}
                    convertToNational={true}
                    inputStyle={{ backgroundColor: '#fff', borderBottom: '0px'}}
                  />
                </div>
              </div>
            </div>
          </div>
          <br/>
          <br/>
          <div className="row justify-content-center" style={{height: 250}}>
            <div className="col-10">
              <table className="tborder" style={{width: '98%', height: '100%'}}>
                <tbody className="tborder">
                  <tr className="tborder">
                    <th className="tborder">CATEGORY</th>
                    <th className="tborder">COLLECTION</th>
                    <th className="tborder">SERVICE FEE</th>
                    <th className="tborder">RETURNING</th>
                  </tr>
                  <tr className="tborder">
                    <td className="tborder">
                      E.F.T. (PAD)
                    </td>
                    <td className="tborder">
                      <FormattedNumber
                        value={this.getSelectedStatement().eftCollected}
                        style="currency"
                        currency="USD"
                      />
                    </td>
                    <td className="tborder">
                      <FormattedNumber
                        value={this.getSelectedStatement().eftServiceCharge}
                        style="currency"
                        currency="USD"
                      />
                    </td>
                    <td className="tborder">
                      <FormattedNumber
                        value={this.getSelectedStatement().eftReturning}
                        style="currency"
                        currency="USD"
                      />
                    </td>
                  </tr>
                  <tr className="tborder">
                    <td className="tborder">
                      CREDIT CARD
                    </td>
                    <td className="tborder">
                      <FormattedNumber
                        value={this.getSelectedStatement().ccCollected}
                        style="currency"
                        currency="USD"
                      />
                    </td>
                    <td className="tborder">
                      <FormattedNumber
                        value={this.getSelectedStatement().ccServiceCharge}
                        style="currency"
                        currency="USD"
                      />
                    </td>
                    <td className="tborder">
                      <FormattedNumber
                        value={this.getSelectedStatement().ccReturning}
                        style="currency"
                        currency="USD"
                      />
                    </td>
                  </tr>
                  <tr className="tborder">
                    <td className="tborder">
                      POINT OF SALE
                    </td>
                    <td className="tborder">
                      <FormattedNumber
                        value={this.getSelectedStatement().posCollected}
                        style="currency"
                        currency="USD"
                      />
                    </td>
                    <td className="tborder">
                      <FormattedNumber
                        value={this.getSelectedStatement().posServiceCharge}
                        style="currency"
                        currency="USD"
                      />
                    </td>
                    <td className="tborder">
                      <FormattedNumber
                        value={this.getSelectedStatement().posReturning}
                        style="currency"
                        currency="USD"
                      />
                    </td>
                  </tr>
                  <tr className="tborder">
                    <td className="tborder">
                      Additional Service
                    </td>
                    <td className="tborder">
                      <FormattedNumber
                        value={0}
                        style="currency"
                        currency="USD"
                      />
                    </td>
                    <td className="tborder">
                      <FormattedNumber
                        value={this.getSelectedStatement().extraServiceCharge}
                        style="currency"
                        currency="USD"
                      />
                    </td>
                    <td className="tborder">
                      <FormattedNumber
                        value={0 - this.getSelectedStatement().extraServiceCharge}
                        style="currency"
                        currency="USD"
                      />
                    </td>
                  </tr>
                  <tr className="tborder">
                    <td className="tborder text-primary">
                      TOTAL
                    </td>
                    <td className="tborder text-primary">
                      <FormattedNumber
                        value={
                          this.getSelectedStatement().eftCollected +
                          this.getSelectedStatement().ccCollected +
                          this.getSelectedStatement().posCollected
                        }
                        style="currency"
                        currency="USD"
                      />
                    </td>
                    <td className="tborder text-primary">
                      <FormattedNumber
                        value={
                          this.getSelectedStatement().eftServiceCharge +
                          this.getSelectedStatement().ccServiceCharge +
                          this.getSelectedStatement().posServiceCharge +
                          this.getSelectedStatement().extraServiceCharge
                        }
                        style="currency"
                        currency="USD"
                      />
                    </td>
                    <td className="tborder text-primary">
                      <FormattedNumber
                        value={
                          this.getSelectedStatement().eftReturning +
                          this.getSelectedStatement().ccReturning +
                          this.getSelectedStatement().posReturning -
                          this.getSelectedStatement().extraServiceCharge
                        }
                        style="currency"
                        currency="USD"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <br/>
          <br/>
          <div className="row align-items-center">
            <div className="col-12 text-center blue">
              THANK YOU FOR CHOOSING MIMOSA SOLUTIONS INC.
            </div>
          </div>
        </div>
      );
    } else {
      return(null);
    }
  }

  private renderAddedTags = (newTag: string, index: number): JSX.Element => {
    const { authState, adminState } = this.props;

    if (newTag !== '') {
      return(
        <Chip
          key={index}
          onRequestDelete={() => cannotWrite(BUSINESS, authState, adminState) && 
            cannotUpdate(BUSINESS, authState, adminState) ?
              null :
              this.handleTagDelete(newTag)
          }
          style={{margin: 4, float: 'left'}}
        >
          {newTag}
        </Chip>
      );
    } else {
      return(null);
    }
  }

  private renderAddedEmails = (newEmail: string, index: number): JSX.Element => {
    if (newEmail !== '') {
      return(
        <Chip
          key={index}
          onRequestDelete={() => this.handleEmailDelete(newEmail)}
          style={{margin: 4, float: 'left'}}
        >
          {newEmail}
        </Chip>
      );
    } else {
      return(null);
    }
  }

  private renderAddedPhoneNumbers = (newPhoneNumber: string, index: number): JSX.Element => {
    if (newPhoneNumber !== '') {
      return(
        <Chip
          key={index}
          onRequestDelete={() => this.handlePhoneNumberDelete(newPhoneNumber)}
          style={{margin: 4, float: 'left'}}
        >
          {newPhoneNumber}
        </Chip>
      );
    } else {
      return(null);
    }
  }

  private handleTagDelete = (newTag: string): void =>
    this.setState({
      editedTags: this.state.editedTags.filter((t: string) => newTag !== t)
    })

  private handleEmailDelete = (newEmail: string): void =>
    this.setState({
      editedFailedCheckinNotificationCcEmails: this.state.editedFailedCheckinNotificationCcEmails.filter(
        (t: string) => newEmail !== t)
    })

  private handlePhoneNumberDelete = (newPhoneNumber: string): void =>
    this.setState({
      editedFailedCheckinNotificationCcPhoneNumbers: this.state.editedFailedCheckinNotificationCcPhoneNumbers.filter(
        (t: string) => newPhoneNumber !== t)
    })

  private getSelectedStatement = (): Statement => this.props.businessState.statements[this.state.selectedRow];

  private printStatement = (): void => {
    const input = document.getElementById('divToPrint');
    html2canvas(input)
    .then((canvas) => {
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jspdf();
      pdf.addImage(imgData, 'JPEG', 0, 0, 210, 297);
      const date = new Date(this.getSelectedStatement().returnedDate);
      pdf.save('statement-' + (date.getMonth() + 1) + '-' + date.getDate() + '-' + date.getFullYear() + '.pdf');
      this.setState({downloadPdf: false, downloading: false, openPdfPreviewModal: false});
    });
  }

  private handleNextPageClick = (): void => this.setState({currentPage: this.state.currentPage + 1});

  private handlePreviousPageClick = (): void => this.setState({currentPage: this.state.currentPage - 1});

  private isMobileNumberEdited = (): boolean => {
    const { addressInfo, editedAddressInfo } = this.state;
    return addressInfo.mobile !== editedAddressInfo.mobile;
  }

  private isPhoneNumberEdited = (): boolean => {
    const { addressInfo, editedAddressInfo } = this.state;
    return addressInfo.phone !== editedAddressInfo.phone;
  }

  private setPhoneErrorMessage = (businessState: BusinessState): void => {
    const { phoneNumberValidationStatus } = businessState.validation;

    this.setState({
      addressInfoError: {
        ...this.state.addressInfoError,
        phoneError: phoneNumberValidationStatus && phoneNumberValidationStatus === 409 ?
          'The phone number is already taken' :
          ''
    }});
  }

  private handleBankAccountNumberChange = (event: React.FormEvent<HTMLInputElement>, newValue: string): void =>
    this.setState({
      editedBankAccountNumber: newValue === '' ? undefined : newValue,
      editedTransitNumber: this.state.editedTransitNumber && this.state.editedTransitNumber.includes('X') ?
        '' :
        this.state.editedTransitNumber,
      bankAccountNumberErrorMessage: this.validateBankAccountNumber(newValue),
      updated: false
    })
  
  private validateBankAccountNumber = (newValue: string): string => {
    return !validator.isNumeric(newValue) ?
      'Please enter a valid bank account number.' :
      newValue.length > MAX_BANK_ACCOUNT_LENGTH ? 
        `Bank Account Number cannot be longer than ${MAX_BANK_ACCOUNT_LENGTH} digits` : 
        '';
  }

  private handleFinancialInstitutionNumberChange = (event: React.FormEvent<HTMLInputElement>, newValue: string): void =>
    this.setState({
      editedFinancialInstitutionNumber: newValue === '' ? undefined : newValue,
      editedTransitNumber: this.state.editedTransitNumber && this.state.editedTransitNumber.includes('X') ?
        '' :
        this.state.editedTransitNumber,
      editedBankAccountNumber: this.state.editedBankAccountNumber && this.state.editedBankAccountNumber.includes('X') ?
        '' :
        this.state.editedBankAccountNumber,
      financialInstitutionNumberErrorMessage: this.validateFinancialInstitutionNumber(newValue),
      updated: false
    })
  
  private validateFinancialInstitutionNumber = (newValue: string): string => {
    return !validator.isNumeric(newValue) ?
      'Please enter a valid financial institution number.' :
      newValue.length !== FINANCIAL_INSTITUTION_LENGTH ? 
        `Financial Institution Number / Bank Code has to be ${FINANCIAL_INSTITUTION_LENGTH} digit` : 
        '';
  }

  private handleTransitNumberChange = (event: React.FormEvent<HTMLInputElement>, newValue: string): void =>
    this.setState({
      editedTransitNumber: newValue === '' ? undefined : newValue,
      editedBankAccountNumber: this.state.editedBankAccountNumber && this.state.editedBankAccountNumber.includes('X') ?
        '' :
        this.state.editedBankAccountNumber,
      transitNumberErrorMessage: this.validateTransitNumber(newValue),
      updated: false
    })

  private validateTransitNumber = (newValue: string): string => {
    return !validator.isNumeric(newValue) ?
      'Please enter a valid transit number.' :
      newValue.length !== TRANSIT_NUMBER_LENGTH ? 
        `Transit Number has to be ${TRANSIT_NUMBER_LENGTH} digit` : 
        '';      
  }

  private isThereDirectDeposit = (props: Props): boolean =>
    props.businessState !== undefined && props.businessState !== null &&
      props.businessState.business !== undefined && props.businessState.business !== null && 
      props.businessState.business.directDeposit !== undefined

  private isThereCreditCard = (props: Props): boolean =>
    props.businessState !== undefined && props.businessState !== null &&
      props.businessState.business !== undefined && props.businessState.business !== null && 
      props.businessState.business.creditCard !== undefined

  private handleAccountHolderNameChange = (event: React.FormEvent<HTMLInputElement>, newValue: string): void =>
    this.setState((prevState: States) => ({
      editedAccountHolderName: newValue === '' ? undefined : newValue,
      editedTransitNumber: prevState.editedTransitNumber.includes('X') ? '' : prevState.editedTransitNumber,
      transitNumberErrorMessage: prevState.editedTransitNumber.includes('X') ? 
        'Enter transit number' : 
        prevState.transitNumberErrorMessage,
      editedBankAccountNumber: prevState.editedBankAccountNumber.includes('X') ?
        '' :
        prevState.editedBankAccountNumber,
      bankAccountNumberErrorMessage: prevState.editedBankAccountNumber.includes('X') ?
        'Enter bank account number' :
        prevState.bankAccountNumberErrorMessage,
      accountHolderNameErrorMessage: isValidName(newValue) ? '' : 'Please enter a valid account holder name.',
      updated: false
    }))

  private handleFinancialInstitutionNameChange = (event: React.FormEvent<HTMLInputElement>, newValue: string): void =>
    this.setState({
      editedFinancialInstitutionName: newValue === '' ? undefined : newValue,
      editedTransitNumber: this.state.editedTransitNumber.includes('X') ? '' : this.state.editedTransitNumber,
      editedBankAccountNumber: this.state.editedBankAccountNumber.includes('X') ?
        '' :
        this.state.editedBankAccountNumber,
      financialInstitutionNameErrorMessage: isValidBusinessName(newValue) ? 
        '' : 
        'Please enter a valid financial institution name.',
      updated: false
    })

  private handleUnselectImage = (): void => this.setState({businessLogoFile: undefined});

  private handleBusinessLogoFileChange = (fileFormatError: string, fileSizeError: string, newFile: File): void => 
    this.setState({businessLogoFile: newFile, fileSizeError: fileSizeError, fileFormatError: fileFormatError})

  private isFieldEdited = (states: States): boolean => {
    const { 
      name, editedName, servicePackage, editedServicePackage, editedAddressInfo, addressInfo, businessLogoFile,
      pointPerAttendance, editedPointPerAttendance, smsDelay, editedSmsDelay, editedFailedCheckinNotificationCcEmails,
      editedFailedCheckinNotificationCcPhoneNumbers
    } = states;

    return name !== editedName || servicePackage !== editedServicePackage ||
      this.isAddressInfoChanged(addressInfo, editedAddressInfo) || businessLogoFile !== undefined ||
      pointPerAttendance !== editedPointPerAttendance || this.isDirectDepositInfoEdited() ||
      this.isCreditCardInfoEdited() ||
      this.state.editedTags.filter((t: string) => !this.state.tags.includes(t)).length > 0 ||
      this.state.tags.filter((t: string) => !this.state.editedTags.includes(t)).length > 0 ||
      this.state.failedCheckinEmail !== this.state.editedFailedCheckinEmail ||
      this.state.failedCheckinSms !== this.state.editedFailedCheckinSms ||
      this.state.failedCheckinNotificationCcEmails !== this.state.editedFailedCheckinNotificationCcEmails ||
      this.state.failedCheckinNotificationCcPhoneNumbers !== this.state.editedFailedCheckinNotificationCcPhoneNumbers ||
      smsDelay !== editedSmsDelay || 
      this.state.timeBasedMultipleCheckin !== this.state.editedTimeBasedMultipleCheckin ||
      this.state.sendCheckinNotificationToMobile2 !== this.state.editedSendCheckinNotificationToMobile2;
  }

  private isAddressInfoChanged = (original: AddressInfo, edited: AddressInfo): boolean => {
    return original.address !== edited.address || original.city !== edited.city ||
      original.country !== edited.country ||
      original.mobile !== edited.mobile || original.phone !== edited.phone ||
      original.postalCode.toUpperCase() !== edited.postalCode.toUpperCase() || original.province !== edited.province;
  }

  private onUpdateClick = (): void => {
    const { updateBusiness } = this.props;
    const { 
      editedName, editedAddressInfo, editedServicePackage, editedAccountHolderName, editedFinancialInstitutionName,
      editedTransitNumber, editedFinancialInstitutionNumber, editedBankAccountNumber, businessLogoFile,
      editedPointPerAttendance, editedCardNumber, editedCardHolderName, editedCvc, editedExpiryMonth, editedExpiryYear
    } = this.state;

    this.setState(
      {loading: !isAuthTokenExpired()},
      () => updateBusiness({
        clientId: this.props.authState.account!.id,
        name: editedName === '' ? undefined : editedName,
        address: editedAddressInfo.address === '' ? undefined : editedAddressInfo.address,
        city: editedAddressInfo.city === '' ? undefined : editedAddressInfo.city,
        province: editedAddressInfo.province === '' ? undefined : editedAddressInfo.province,
        country: editedAddressInfo.country === '' ? undefined : editedAddressInfo.country,
        postalCode: editedAddressInfo.postalCode === '' ? undefined : editedAddressInfo.postalCode,
        mobile: editedAddressInfo.mobile === '' ? undefined : editedAddressInfo.mobile,
        phone: editedAddressInfo.phone === '' ? undefined : editedAddressInfo.phone,
        logoFile: businessLogoFile ? businessLogoFile : undefined,
        servicePackage: editedServicePackage,
        timeZone: moment_timezone.tz.guess(),
        accountHolderName: editedAccountHolderName === '' ? undefined : editedAccountHolderName,
        financialInstitutionName: editedFinancialInstitutionName === '' ? undefined : editedFinancialInstitutionName,
        transitNumber: editedTransitNumber === '' ? undefined : editedTransitNumber,
        financialInstitutionNumber: editedFinancialInstitutionNumber === '' ? 
          undefined : 
          editedFinancialInstitutionNumber,
        bankAccountNumber: editedBankAccountNumber === '' ? undefined : editedBankAccountNumber,
        pointPerAttendance: editedPointPerAttendance,
        directDepositStatus: this.isDirectDepositInfoBlank() ?
          DIRECT_DEPOSIT_REMAINED :
          this.selectDirectDepositStatus(),
        creditCardStatus: this.isCreditCardInfoBlank() ?
          CREDIT_CARD_REMAINED :
          this.selectCreditCardStatus(),
        cardNumber: editedCardNumber === '' ? undefined : editedCardNumber,
        cardHolderName: editedCardHolderName === '' ? undefined : editedCardHolderName,
        cvc: editedCvc === '' ? undefined : editedCvc,
        expiryMonth: editedExpiryMonth === '' ? undefined : editedExpiryMonth,
        expiryYear: editedExpiryYear === '' ? undefined : editedExpiryYear,
        directDepositId: this.getDirectDepositIdForUpdating(),
        creditCardId: this.getCreditCardIdForUpdating(),
        tags: this.state.editedTags,
        failedCheckinNotificationCcEmails: this.state.editedFailedCheckinNotificationCcEmails,
        failedCheckinNotificationCcPhoneNumbers: this.state.editedFailedCheckinNotificationCcPhoneNumbers,
        failedCheckinEmail: this.state.editedFailedCheckinEmail,
        failedCheckinSms: this.state.editedFailedCheckinSms,
        smsDelay: this.state.editedSmsDelay,
        timeBasedMultipleCheckin: this.state.editedTimeBasedMultipleCheckin,
        sendCheckinNotificationToMobile2: this.state.editedSendCheckinNotificationToMobile2
      })
      .then(response => 
        this.setState({updated: true, loading: false})
      )
    );
  }

  private getCreditCardIdForUpdating = (): number | undefined => {
    if (this.isThereCreditCard(this.props)) {
      return this.props.businessState.business!.creditCard!.id;
    } else {
      return undefined;
    }
  }

  private getDirectDepositIdForUpdating = (): number | undefined => {
    if (this.isThereDirectDeposit(this.props)) {
      return this.props.businessState.business!.directDeposit!.id;
    } else {
      return undefined;
    }
  }

  private isThereError = (states: States): boolean => {
    const {
      addressInfoError, nameErrorMessage, accountHolderNameErrorMessage, financialInstitutionNameErrorMessage,
      bankAccountNumberErrorMessage, financialInstitutionNumberErrorMessage, transitNumberErrorMessage,
      pointPerAttendanceErrorMessage, cardNumberErrorMessage

    } = states;

    return addressInfoError.addressError !== '' || addressInfoError.cityError !== '' ||
      addressInfoError.countryError !== '' ||
      addressInfoError.phoneError !== '' || addressInfoError.mobileError !== '' ||
      addressInfoError.postalCodeError !== '' || addressInfoError.provinceError !== '' ||
      nameErrorMessage !== '' || accountHolderNameErrorMessage !== '' || financialInstitutionNameErrorMessage !== '' ||
      bankAccountNumberErrorMessage !== '' || financialInstitutionNumberErrorMessage !== '' ||
      transitNumberErrorMessage !== '' || pointPerAttendanceErrorMessage !== '' ||
      (!this.isCreditCardInfoBlank() && !this.isCreditCardInfoFilled()) || cardNumberErrorMessage !== '';
  }

  private getAddressInfo = (businessState: BusinessState): AddressInfo => {
    return businessState.business ?
    {
      address: businessState.business.address,
      city: businessState.business.city,
      country: this.filterAndGetCountryCode(countries, businessState),
      province: businessState.business.province,
      postalCode: businessState.business.postalCode,
      phone: businessState.business.phone,
      mobile: businessState.business.mobile,
    } :
    {
      address: '',
      city: '',
      country: 'CA',
      province: '',
      postalCode: '',
      phone: '',
      mobile: ''
    };
  }

  private filterAndGetCountryCode = (countryInfos: Array<CountryInfo>, businessState: BusinessState): CountryCode => {
    const filtered = countryInfos.filter((countryInfo: CountryInfo) => 
      countryInfo.name.toLowerCase() === businessState.business!.country.toLowerCase());
    
    return filtered.length > 0 ? filtered[0].code : 'CA';
  }

  private onServicePackageChange = (e: React.SyntheticEvent<{}>, index: number, menuItemValue: ServicePackage) =>
    this.setState({
      editedServicePackage: menuItemValue,
      updated: false
    })

  private handleBusinessNameChange = (event: React.FormEvent<HTMLInputElement>, newValue: string): void =>
    this.setState({
      editedName: newValue,
      nameErrorMessage: isValidBusinessName(newValue) && newValue.length < 30 ? '' : NAME_ERROR_MESSAGE,
      updated: false
    })

  private addressInfoChangeHandler = (editedAddressInfo: AddressInfo): void =>
    this.setState({
      editedAddressInfo: editedAddressInfo,
      updated: false
    })
  
  private handlePointPerAttendance = (event: React.FormEvent<HTMLInputElement>, newValue: string): void =>
    this.setState({
      editedPointPerAttendance: validator.isNumeric(newValue) ? parseInt(newValue, 10) : 0,
      pointPerAttendanceErrorMessage: validator.isNumeric(newValue) ? '' : 'Enter valid numeric value'
    })

  private handleSmsDelays = (event: React.FormEvent<HTMLInputElement>, newValue: string): void =>
    this.setState({
      editedSmsDelay: validator.isNumeric(newValue) ? parseInt(newValue, 10) : 0,
      smsDelaysErrorMessage: validator.isNumeric(newValue) ? '' : 'Enter valid numeric value'
    })

  private renderDirectDepositForm = (): JSX.Element => {
    const {
      editedAccountHolderName, accountHolderNameErrorMessage, editedFinancialInstitutionName,
      financialInstitutionNameErrorMessage, editedTransitNumber, transitNumberErrorMessage,
      editedFinancialInstitutionNumber, financialInstitutionNumberErrorMessage, editedBankAccountNumber,
      bankAccountNumberErrorMessage
    } = this.state;
    const { authState, adminState } = this.props;

    return(
      <div>
        <div className="row pb3">
          <div className="col-12">
            <TextField
              fullWidth={true}
              floatingLabelText="Account Holder Name"
              floatingLabelFixed={true}
              value={editedAccountHolderName}
              onChange={this.handleAccountHolderNameChange}
              disabled={cannotWrite(BUSINESS, authState, adminState) && 
                cannotUpdate(BUSINESS, authState, adminState)}
              errorText={accountHolderNameErrorMessage}
            />
          </div>
        </div>
        <div className="row pb3">
          <div className="col-12">
            <TextField
              fullWidth={true}
              floatingLabelText="Bank Name"
              floatingLabelFixed={true}
              value={editedFinancialInstitutionName}
              onChange={this.handleFinancialInstitutionNameChange}
              disabled={cannotWrite(BUSINESS, authState, adminState) && 
                cannotUpdate(BUSINESS, authState, adminState)}
              errorText={financialInstitutionNameErrorMessage}
            />
          </div>
        </div>
        <div className="row pb3">
          <div className="col-6 pr-2">
            <TextField
              fullWidth={true}
              floatingLabelText="Transit Number"
              floatingLabelFixed={true}
              value={editedTransitNumber}
              onChange={this.handleTransitNumberChange}
              disabled={cannotWrite(BUSINESS, authState, adminState) && 
                cannotUpdate(BUSINESS, authState, adminState)}
              errorText={transitNumberErrorMessage}
            />
          </div>
          <div className="col-6">
            <TextField
              fullWidth={true}
              floatingLabelText="Bank Number"
              floatingLabelFixed={true}
              value={editedFinancialInstitutionNumber}
              onChange={this.handleFinancialInstitutionNumberChange}
              disabled={cannotWrite(BUSINESS, authState, adminState) && 
                cannotUpdate(BUSINESS, authState, adminState)}
              errorText={financialInstitutionNumberErrorMessage}
            />
          </div>
        </div>    
        <div className="row pb3">
          <div className="col-12">
            <TextField
              fullWidth={true}
              floatingLabelText="Bank Account Number"
              floatingLabelFixed={true}
              value={editedBankAccountNumber}
              onChange={this.handleBankAccountNumberChange}
              disabled={cannotWrite(BUSINESS, authState, adminState) && 
                cannotUpdate(BUSINESS, authState, adminState)}
              errorText={bankAccountNumberErrorMessage}
            />
          </div>
        </div>
      </div>
    );
  }

  private renderCreditCardForm = (): JSX.Element => {
    const {
      editedCardNumber,
      cardNumberErrorMessage,
      editedCardHolderName,
      editedExpiryMonth,
      editedExpiryYear,
      editedCvc,
      creditcardType
    } = this.state;

    const { authState, adminState } = this.props;

    return(
      <div>
        <div className="row pb3 align-items-center">
          <div className="col-12">
            <div className="input-group mb-2 mr-sm-2 mb-sm-0" style={{alignItems: 'center'}}>
              {renderCreditCardIcon(creditcardType)}
              <TextField
                fullWidth={true}
                floatingLabelText="Credit Card Number"
                floatingLabelFixed={true}
                value={this.formatCardNumber(editedCardNumber)}
                onChange={this.handleCardNumberChange}
                errorText={cardNumberErrorMessage}
                disabled={cannotWrite(BUSINESS, authState, adminState) && 
                  cannotUpdate(BUSINESS, authState, adminState)}
              />
            </div>
          </div>
        </div>
        <div className="row pb3">
          <div className="col-12">
            <TextField
              fullWidth={true}
              floatingLabelText="Card Holder Name"
              floatingLabelFixed={true}
              value={editedCardHolderName}
              onChange={this.handlecardHolderNameChange}
              disabled={cannotWrite(BUSINESS, authState, adminState) && 
                cannotUpdate(BUSINESS, authState, adminState)}
            />
          </div>
        </div>
        <div className="row pb3">
          <div className="col-6 pr-2">
            <SelectField
              value={editedExpiryMonth}
              hintText="Select Expiry Month"
              floatingLabelText="Expiry Month"
              onChange={this.onExpiryMonthChange}
              style={{width: 150}}
              disabled={cannotWrite(BUSINESS, authState, adminState) && 
                cannotUpdate(BUSINESS, authState, adminState)}
            >
              {CREDIT_CARD_MONTHS.map((month: string, index: number) => {
                return <MenuItem key={index} value={month} primaryText={month} />;
              })}
            </SelectField>
          </div>
          <div className="col-6">
            <SelectField
              value={editedExpiryYear}
              hintText="Select Expiry Year"
              floatingLabelText="Expiry Year"
              onChange={this.onExpiryYearChange}
              style={{width: 150}}
              disabled={cannotWrite(BUSINESS, authState, adminState) && 
                cannotUpdate(BUSINESS, authState, adminState)}
            >
              {generateCreditCardYears().map((year: string, index: number) => {
                return <MenuItem key={index} value={year} primaryText={year} />;
              })}
            </SelectField>
          </div>
        </div>
        <div className="row pb3">
          <div className="col-12">
            <TextField
              fullWidth={true}
              floatingLabelText="CVC"
              floatingLabelFixed={true}
              value={editedCvc}
              onChange={this.handleCvcChange}
              disabled={cannotWrite(BUSINESS, authState, adminState) && 
                cannotUpdate(BUSINESS, authState, adminState)}
            />
          </div>
        </div>
      </div>
    );
  }

  private handleCardNumberChange = (event: React.FormEvent<HTMLInputElement>, newValue: string): void => {
    const trimmedValue = newValue.replace(/\s/g, '');

    this.setState({
      editedCardNumber: trimmedValue === '' ? undefined : trimmedValue,
      cardNumberErrorMessage: (
        (trimmedValue.length === CREDIT_CARD_LENGTH &&
          trimmedValue.charAt(0) !== '3' && trimmedValue.charAt(1) !== '7') || 
        (trimmedValue.length === AMEX_LENGTH &&
          trimmedValue.charAt(0) === '3' && trimmedValue.charAt(1) === '7')) && 
        validateCreditCardNumber(trimmedValue) ?
          '' :
          'Enter valid creditcard number',
      creditcardType: (
        (trimmedValue.length === CREDIT_CARD_LENGTH &&
          trimmedValue.charAt(0) !== '3' && trimmedValue.charAt(1) !== '7') || 
        (trimmedValue.length === AMEX_LENGTH &&
          trimmedValue.charAt(0) === '3' && trimmedValue.charAt(1) === '7')) && 
        validateCreditCardNumber(trimmedValue) ?
          parseCreditCardType(trimmedValue) :
          NONE
    });
  }

  private handlecardHolderNameChange = (event: React.FormEvent<HTMLInputElement>, newValue: string): void =>
    this.setState({
      editedCardHolderName: newValue === '' ? undefined : newValue,
      editedCardNumber: this.state.editedCardNumber.includes('*') ? '' : this.state.editedCardNumber
    })

  private handleCvcChange = (event: React.FormEvent<HTMLInputElement>, newValue: string): void =>
    this.setState({
      editedCvc: newValue === '' ? undefined : newValue
    })

  private onExpiryYearChange = (e: React.SyntheticEvent<{}>, index: number, menuItemValue: string): void =>
    this.setState({
      editedExpiryYear: menuItemValue
    })

  private onExpiryMonthChange = (e: React.SyntheticEvent<{}>, index: number, menuItemValue: string): void =>
    this.setState({
      editedExpiryMonth: menuItemValue
    })

  private isCreditCardInfoFilled = (): boolean => {
    const {
      editedCardNumber, editedCardHolderName, editedCvc, editedExpiryMonth, editedExpiryYear, cardNumber
    } = this.state;

    if (cardNumber && cardNumber.includes('*')) {
      return editedCardHolderName !== undefined &&
        editedCardNumber !== undefined &&
        editedExpiryMonth !== undefined &&
        editedExpiryYear !== undefined;
    } else {
    return editedCardHolderName !== undefined &&
      editedCardNumber !== undefined &&
      editedCvc !== undefined &&
      editedExpiryMonth !== undefined &&
      editedExpiryYear !== undefined;
  }
  }

  private isCreditCardInfoBlank = (): boolean => {
    const { 
      cardNumber, editedCardNumber, editedCardHolderName, editedCvc, editedExpiryMonth, editedExpiryYear,
      cardHolderName, cvc, expiryMonth, expiryYear
    } = this.state;

    if (cardNumber && cardNumber.includes('*')) {
      return !editedCardHolderName && !editedCardNumber && !editedExpiryMonth && !editedExpiryYear &&
        !cardNumber && !cardHolderName && !expiryMonth && !expiryYear;
    } else {
    return !editedCardHolderName && !editedCardNumber && !editedCvc && !editedExpiryMonth && !editedExpiryYear &&
      !cardNumber && !cardHolderName && !cvc && !expiryMonth && !expiryYear;
  }
  }

  private selectCreditCardStatus = (): CreditCardStatus => {
    const { cardNumber, cardHolderName, cvc, expiryMonth, expiryYear } = this.state;
    
    if (!cardNumber && !cardHolderName && !cvc && !expiryMonth && !expiryYear) {
      return CREDIT_CARD_CREATED;
    }

    if (this.isCreditCardInfoEdited()) {
      return CREDIT_CARD_UPDATE;
    }

    return CREDIT_CARD_REMAINED;
  }

  private isCreditCardInfoEdited = (): boolean => {
    const {
      cardNumber, editedCardNumber, cardHolderName, editedCardHolderName, cvc, editedCvc, expiryMonth,
      editedExpiryMonth, expiryYear, editedExpiryYear
    } = this.state;

    return cardNumber !== editedCardNumber || cardHolderName !== editedCardHolderName || cvc !== editedCvc ||
      expiryMonth !== editedExpiryMonth || expiryYear !== editedExpiryYear;
  }

  private selectDirectDepositStatus = (): DirectDepositStatus => {
    const {
      accountHolderName, bankAccountNumber, transitNumber, financialInstitutionName, financialInstitutionNumber
    } = this.state;
    
    if (
      !accountHolderName &&
      !bankAccountNumber &&
      !transitNumber &&
      !financialInstitutionName &&
      !financialInstitutionNumber
    ) {
      return DIRECT_DEPOSIT_CREATED;
    }

    if (this.isDirectDepositInfoEdited()) {
      return DIRECT_DEPOSIT_UPDATE;
    }

    return DIRECT_DEPOSIT_REMAINED;
  }

  private isDirectDepositInfoEdited = (): boolean => {
    const {
      accountHolderName, editedAccountHolderName, bankAccountNumber, editedBankAccountNumber, transitNumber,
      editedTransitNumber, financialInstitutionName, editedFinancialInstitutionName,
      financialInstitutionNumber, editedFinancialInstitutionNumber
    } = this.state;

    return accountHolderName !== editedAccountHolderName || bankAccountNumber !== editedBankAccountNumber ||
      transitNumber !== editedTransitNumber || financialInstitutionName !== editedFinancialInstitutionName ||
      financialInstitutionNumber !== editedFinancialInstitutionNumber;
  }

  private isDirectDepositInfoBlank = (): boolean => {
    const {
      editedAccountHolderName, editedFinancialInstitutionName, editedFinancialInstitutionNumber, editedTransitNumber,
      editedBankAccountNumber, accountHolderName, financialInstitutionName, financialInstitutionNumber,
      transitNumber, bankAccountNumber
    } = this.state;

    return !editedAccountHolderName && 
      !editedFinancialInstitutionName && 
      !editedFinancialInstitutionNumber &&
      !editedTransitNumber &&
      !editedBankAccountNumber &&
      !accountHolderName &&
      !financialInstitutionName &&
      !financialInstitutionNumber &&
      !transitNumber &&
      !bankAccountNumber;
  }

  private handleFailedCheckinEmailCheckboxChange = (
    event: React.MouseEvent<HTMLInputElement>,
    isInputChecked: boolean): void => {
      this.setState({
        editedFailedCheckinEmail: isInputChecked
      });
    }

  private handleFailedCheckinSmsCheckboxChange = (
    event: React.MouseEvent<HTMLInputElement>,
    isInputChecked: boolean): void => {
      this.setState({
        editedFailedCheckinSms: isInputChecked
      });
    }

  private handleTimeBasedMultipleCheckInCheckboxChange = (
    event: React.MouseEvent<HTMLInputElement>,
    isInputChecked: boolean): void => {
      this.setState({
        editedTimeBasedMultipleCheckin: isInputChecked
      });
    }

  private handleSendSMSToMobile2CheckboxChange = (
    event: React.MouseEvent<HTMLInputElement>,
    isInputChecked: boolean): void => {
      this.setState({
        editedSendCheckinNotificationToMobile2: isInputChecked
      });
    }

  private formatCardNumber = (cardNumber: string): string => {
    if (cardNumber && validator.isNumeric(cardNumber)) {
      return creditcardutils.formatCardNumber(cardNumber);
    }
    return cardNumber;
  }
}

export default BusinessInfoComponent;



// WEBPACK FOOTER //
// ./src/account/BusinessInfoComponent.tsx